import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { guestApi } from '@/api/guest';

export function GuestSignPage() {
  const { t } = useTranslation();
  const { documentId } = useParams<{ documentId: string }>();
  const id = Number(documentId);

  const info = useQuery({
    queryKey: ['guest-sign', id],
    queryFn: () => guestApi.signInfo(id),
    retry: false,
  });

  const initiate = useMutation({
    mutationFn: () => guestApi.initiateSign(id),
  });

  const [status, setStatus] = useState<string | null>(null);
  const [expireAt, setExpireAt] = useState<Date | null>(null);

  useEffect(() => {
    if (!initiate.data) return;
    setExpireAt(new Date(initiate.data.expire_at));
    const interval = window.setInterval(async () => {
      try {
        const res = await guestApi.pollSignStatus(id);
        setStatus(res.status);
        if (res.status !== 'waiting') window.clearInterval(interval);
        if (expireAt && new Date() > expireAt) window.clearInterval(interval);
      } catch {
        /* ignore */
      }
    }, 3000);
    return () => window.clearInterval(interval);
  }, [initiate.data, id, expireAt]);

  if (info.isLoading) return <Spinner />;
  if (info.error) {
    return (
      <div className="mx-auto max-w-xl p-4">
        <Card title={t('errors.link_expired')}>
          <p className="text-sm text-slate-600">{t('errors.link_expired_body')}</p>
        </Card>
      </div>
    );
  }
  if (!info.data) return null;

  return (
    <div className="mx-auto max-w-xl space-y-4 p-4">
      <Card title={t('acts.sign_title')}>
        <iframe
          src={info.data.pdf_url}
          title="Document PDF"
          className="mb-3 h-80 w-full rounded-lg border border-slate-200"
        />
        {initiate.data ? (
          <>
            <img src={initiate.data.qrCode} alt="QR" className="mx-auto h-48 w-48" />
            <p className="mt-3 text-center text-xs text-slate-500">
              {t('web.desktop.scan_qr_with_egov')}
            </p>
            {status === 'done' ? (
              <p className="mt-3 text-center text-green-700">{t('acts.signed_successfully')}</p>
            ) : null}
          </>
        ) : (
          <Button onClick={() => initiate.mutate()} loading={initiate.isPending}>
            {t('acts.sign_with_ecp')}
          </Button>
        )}
      </Card>
    </div>
  );
}
